import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api from "@/lib/api";
import { FloatingAiAssistant } from "@/components/AiAssistant";
import {
  LayoutDashboard, Package, CalendarClock, Trash2, Thermometer, ClipboardCheck,
  AlertTriangle, Bell, ChefHat, LogOut, Languages, Gem,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, key: "nav.dashboard", testid: "nav-dashboard" },
  { to: "/magazzino", icon: Package, key: "nav.products", testid: "nav-products" },
  { to: "/scadenze", icon: CalendarClock, key: "nav-expirations" },
  { to: "/sprechi", icon: Trash2, key: "nav.waste", testid: "nav-waste" },
  { to: "/temperature", icon: Thermometer, key: "nav.temperatures", testid: "nav-temperatures" },
  { to: "/haccp", icon: ClipboardCheck, key: "nav.haccp", testid: "nav-haccp" },
  { to: "/non-conformita", icon: AlertTriangle, key: "nav.nonconformities", testid: "nav-nonconformities" },
  { to: "/prezzi", icon: Gem, key: "nav.pricing", testid: "nav-pricing" },
];

export default function AppShell() {
  const { data, logout, locationId, selectLocation } = useAuth();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  const user = data?.user;
  const locations = data?.locations || [];

  useEffect(() => {
    let mounted = true;
    const load = () => api.get("/notifications", { params: { location_id: locationId } })
      .then((r) => { if (mounted) setUnread(r.data.filter((n) => !n.read).length); })
      .catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(id); };
  }, [locationId]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background" data-testid="app-shell">
      <header className="sticky top-0 z-40 bg-card/90 backdrop-blur-md border-b">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-3">
          <NavLink to="/dashboard" className="flex items-center gap-2 shrink-0" data-testid="app-header-logo">
            <img src="/logo.png" alt="T2WORK KITCHEN" className="h-10 w-10 rounded-xl object-contain bg-white border" />
            <span className="font-display font-extrabold text-lg hidden sm:block">{t("appName")}</span>
          </NavLink>

          {locations.length > 1 && (
            <select
              data-testid="location-selector"
              value={locationId || ""}
              onChange={(e) => selectLocation(e.target.value)}
              className="h-9 rounded-lg border bg-background px-2 text-sm font-medium"
            >
              {locations.map((l) => (
                <option key={l.location_id} value={l.location_id}>{l.name}</option>
              ))}
            </select>
          )}

          <div className="flex-1" />

          <button
            data-testid="kitchen-mode-toggle"
            onClick={() => navigate("/kitchen")}
            className="tactile-btn h-10 px-3 rounded-xl bg-slate-900 text-white flex items-center gap-2 text-sm font-semibold"
          >
            <ChefHat className="h-4 w-4" />
            <span className="hidden md:inline">{t("nav.kitchen")}</span>
          </button>

          <button
            data-testid="notifications-bell"
            onClick={() => navigate("/notifiche")}
            className="tactile-btn relative h-10 w-10 rounded-xl border bg-background flex items-center justify-center"
          >
            <Bell className="h-4 w-4" />
            {unread > 0 && (
              <span data-testid="notifications-badge" className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
                {unread}
              </span>
            )}
          </button>

          <button
            data-testid="language-toggle-button"
            onClick={toggle}
            className="tactile-btn h-10 px-3 rounded-xl border bg-background flex items-center gap-1.5 text-xs font-bold uppercase"
          >
            <Languages className="h-4 w-4" />
            {lang}
          </button>

          <div className="hidden sm:flex items-center gap-2 pl-2 border-l">
            <div className="text-right">
              <p className="text-sm font-semibold leading-tight">{user?.name}</p>
              <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{user?.role}</p>
            </div>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="tactile-btn h-10 w-10 rounded-xl border bg-background flex items-center justify-center text-muted-foreground hover:text-red-600"
              title={t("logout")}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 pb-28 md:pb-10">
        <Outlet />
      </main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card border-t" data-testid="mobile-bottom-nav">
        <div className="flex overflow-x-auto">
          {NAV.map(({ to, icon: Icon, key }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`mobile-${key.replace(".", "-")}`}
              className={({ isActive }) =>
                `flex-1 min-w-16 flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <Icon className="h-5 w-5" />
              <span className="truncate max-w-16">{t(key)}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      <aside className="hidden md:block fixed left-4 top-24 bottom-6 w-14 z-30" data-testid="desktop-side-nav">
        <div className="h-full bg-card border rounded-2xl shadow-sm flex flex-col items-center py-3 gap-1">
          {NAV.map(({ to, icon: Icon, key, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid || `side-${key.replace(".", "-")}`}
              title={t(key)}
              className={({ isActive }) =>
                `tactile-btn h-11 w-11 rounded-xl flex items-center justify-center ${
                  isActive ? "bg-primary text-white" : "text-muted-foreground hover:bg-secondary"
                }`
              }
            >
              <Icon className="h-5 w-5" />
            </NavLink>
          ))}
        </div>
      </aside>
      <div className="hidden md:block md:pl-20" />
      <FloatingAiAssistant />
    </div>
  );
}
